import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Euro,
    FileText,
    TrendingUp,
    TrendingDown
} from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { api } from '../services/api';
import { User, Asset, AssetCategory } from '../types';
import { formatCurrency } from '../utils/formatters';

interface CardProps {
    title: string;
    value: string;
    trend?: string;
    trendUp?: boolean;
    subtitle?: string;
    icon: React.ElementType;
}

const ReportCard: React.FC<CardProps> = ({ title, value, trend, trendUp, subtitle, icon: Icon }) => (
    <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col justify-between h-40 relative overflow-hidden group hover:shadow-md transition-all">
        <div className="flex justify-between items-start">
            <div>
                <p className="text-slate-500 text-sm font-bold mb-1">{title}</p>
                <h3 className="text-3xl font-black text-slate-800 tracking-tight">{value}</h3>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-sky-50 flex items-center justify-center text-sky-600 group-hover:scale-110 transition-transform">
                <Icon size={24} strokeWidth={2} />
            </div>
        </div>

        <div className="mt-auto">
            {trend && (
                <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-bold ${trendUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                    {trendUp ? <TrendingUp size={14} className="mr-1" /> : <TrendingDown size={14} className="mr-1" />}
                    {trend}
                    <span className="text-slate-400 ml-1 font-normal">vs last year</span>
                </div>
            )}
            {subtitle && (
                <div className="text-slate-400 text-xs font-medium mt-1">
                    {subtitle}
                </div>
            )}
        </div>
    </div>
);

interface AssetReportsViewProps {
    user: User;
}

export const AssetReportsView: React.FC<AssetReportsViewProps> = ({ user }) => {
    const { t } = useTranslation('assets');

    const [assets, setAssets] = useState<Asset[]>([]);
    const [categories, setCategories] = useState<AssetCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedBranchId, setSelectedBranchId] = useState<string>(
        user.role === 'agency' || user.role === 'agency_member' ? '' : user.id
    );
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [branches, setBranches] = useState<User[]>([]);

    const isAssetManager = user.memberProfile?.teamRole === 'asset_manager';

    useEffect(() => {
        if (user.role !== 'agency' && user.role !== 'agency_member') {
            setSelectedBranchId(user.id);
        }
    }, [user.id]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const agencyId = user.agencyId || user.id; // If agency, use id. If member, use agencyId.
                if (!agencyId) return;

                const filters: any = {};
                if (selectedBranchId) filters.branchId = selectedBranchId;
                if (selectedStatus) filters.status = selectedStatus;

                // Asset Manager Restriction: Only see own assets
                // Note: If Asset Manager selects a branch, we filter by that branch. 
                // But we should ensuring they are assigned to it? 
                // getAgencyClients already filters the 'branches' list they can see.
                // But for explicit 'assetManagerId' filter on assets?
                if (isAssetManager) {
                    filters.assetManagerId = user.id;
                }

                const [assetsResponse, categoriesData, clientsData] = await Promise.all([
                    api.getAssets(agencyId, null, 1000, filters), // Fetch up to 1000 assets for report with filter
                    api.getAssetCategories(agencyId),
                    // If Asset Manager, fetch only assigned clients (branches)
                    (user.role === 'agency' || user.role === 'agency_member')
                        ? api.getAgencyClients(agencyId, isAssetManager ? user.id : undefined)
                        : Promise.resolve([user] as User[])
                ]);

                setAssets(assetsResponse.data);
                setCategories(categoriesData);

                // Filter out agency members, keeping only clients (Branches)
                // Note: getAgencyClients(..., assignedAgentId) returns users who have 'assignedAgentIds' containing the ID.
                // These are typically Clients.
                const clientBranches = clientsData.filter(u => u.role !== 'agency_member' && u.role !== 'agency');
                setBranches(clientBranches);

            } catch (error) {
                console.error("Failed to fetch report data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [user.id, user.agencyId, selectedBranchId, selectedStatus]);



    // Calculations
    const totalAssets = assets.length;
    const totalValue = assets.reduce((sum, asset) => sum + (asset.totalValue || 0), 0);

    // Depreciation Calculation (Simplified: 10% flat for now as placeholder if no calc exists, or sum 'depreciatedValue' if computed?) 
    // Since we don't have a robust depreciation engine yet, I will simulate it based on Category Rate if avail.
    // Actually, let's just show 0 or a placeholder if we can't calc it accurately.
    // However, the screenshot shows "Current Depreciation".
    // I'll calculate simplistic depreciation: Cost * (CategoryRate || 0.10) * AgeInYears.
    // NOTE: This is a view-only estimation.
    let totalDepreciation = 0;
    const now = new Date();

    assets.forEach(asset => {
        const cat = categories.find(c => c.id === asset.categoryId);
        const rate = cat ? (cat.depreciationRate / 100) : 0; // e.g. 20% -> 0.2
        if (rate > 0 && asset.acquisitionDate) {
            const acquisition = new Date(asset.acquisitionDate);
            const ageInMilliseconds = now.getTime() - acquisition.getTime();
            const ageInYears = ageInMilliseconds / (1000 * 60 * 60 * 24 * 365.25);
            if (ageInYears > 0) {
                const dep = (asset.totalValue || 0) * rate * ageInYears;
                // Cap at value - residual
                const maxDep = (asset.totalValue || 0) - (asset.residualValue || 0);
                totalDepreciation += Math.min(dep, maxDep);
            }
        }
    });

    // Charts Data
    const categoryStats = categories.map(cat => {
        const catAssets = assets.filter(a => a.categoryId === cat.id);
        const value = catAssets.reduce((sum, a) => sum + (a.totalValue || 0), 0);
        const count = catAssets.length;
        return {
            name: cat.title,
            value: value,
            count: count,
            color: '#3b82f6' // Default, will randomize below
        };
    }).filter(s => s.value > 0).sort((a, b) => b.value - a.value);

    // Donut Colors
    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6366f1'];

    // Prepare Pie Data (Count Distribution)
    const pieData = categoryStats.map((stat, index) => ({
        name: stat.name,
        value: stat.count,
        color: COLORS[index % COLORS.length]
    })).sort((a, b) => b.value - a.value); // Sort pie by count? Or keep same order? Pie usually by size.

    // Update bar data colors if needed, but bar chart usually single color. Pie uses multiple.

    if (loading && assets.length === 0) {
        return <div className="p-8 text-center text-slate-500">Loading reports...</div>;
    }

    // Get Currency
    const currencyCode = user.currency || 'EUR';
    // Logic to select currency icon could be expanded. For now, we default to Euro icon or simple 'CircleDollarSign' if available, 
    // but Lucide 'Euro' is specific. Let's stick to Euro icon visually but format value correctly, 
    // OR ideally switch icon based on currency. 
    // Since 'Euro' is imported, let's keep it but ideally we should import DollarSign, PoundSterling etc.
    // For now, I will just format the string. The icon is decorative. 
    // To be perfect, let's just use the 'Euro' icon as a generic 'Money' icon or rename it? 
    // Actually, let's just use the formatted string.

    return (
        <div className="p-8 max-w-[1600px] mx-auto space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-2">Report Dashboard</h1>
                    <p className="text-slate-500">Επισκόπηση της περιουσίας και των παγίων σας.</p>
                </div>

                {/* Filters */}
                <div className="flex gap-4">
                    <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-500 px-2">Κατάσταση:</span>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="bg-slate-50 border-none rounded-lg py-2 pl-3 pr-8 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                        >
                            <option value="">{t('status.all', 'Όλες')}</option>
                            <option value="active">{t('status.active')}</option>
                            <option value="temporarily_inactive">{t('status.temporarily_inactive')}</option>
                            <option value="inactive">{t('status.inactive')}</option>
                            <option value="fully_depreciated">{t('status.fully_depreciated')}</option>
                            <option value="for_sale">{t('status.for_sale')}</option>
                            <option value="sold">{t('status.sold')}</option>
                            <option value="deleted">{t('status.deleted')}</option>
                            <option value="written_off">{t('status.written_off')}</option>
                            <option value="cancelled">{t('status.cancelled')}</option>
                            <option value="to_be_destroyed">{t('status.to_be_destroyed')}</option>
                            <option value="to_be_moved">{t('status.to_be_moved')}</option>
                        </select>
                    </div>

                    {(user.role === 'agency' || user.role === 'agency_member') && (
                        <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-500 px-2">Υποκατάστημα:</span>
                            <select
                                value={selectedBranchId}
                                onChange={(e) => setSelectedBranchId(e.target.value)}
                                className="bg-slate-50 border-none rounded-lg py-2 pl-3 pr-8 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            >
                                <option value="">Όλα</option>
                                {branches.map(branch => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.profile?.companyName || branch.config?.companyName || branch.displayName || branch.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <ReportCard
                    title="Σύνολο Παγίων"
                    value={totalAssets.toString()}
                    trend="+0" // diff not implemented yet
                    trendUp={true}
                    icon={Box}
                    subtitle="Ενεργά Πάγια"
                />
                <ReportCard
                    title="Συνολική Αξία"
                    value={formatCurrency(totalValue, currencyCode)}
                    trend="+0%"
                    trendUp={true}
                    icon={Euro}
                />
                <ReportCard
                    title="Τρέχουσες Αποσβέσεις"
                    value={formatCurrency(totalDepreciation, currencyCode)}
                    subtitle={`Εκτίμηση ${new Date().getFullYear()}`}
                    icon={FileText}
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-[500px]">
                {/* Horizontal Bar Chart */}
                <div className="lg:col-span-2 bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex flex-col">
                    <div className="flex justify-between items-center mb-8">
                        <h3 className="text-xl font-bold text-slate-800">Αξία Παγίων ανά Κατηγορία</h3>
                        <div className="bg-slate-50 px-4 py-2 rounded-xl text-sm font-bold text-slate-600 cursor-pointer">
                            Όλα τα έτη
                        </div>
                    </div>
                    <div className="flex-1 w-full min-h-[300px]">
                        {categoryStats.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    layout="vertical"
                                    data={categoryStats}
                                    margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                                    <XAxis type="number" hide />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#64748b', fontSize: 13, fontWeight: 700 }}
                                        width={120}
                                    />
                                    <Tooltip
                                        cursor={{ fill: '#f8fafc' }}
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                        formatter={(value: number) => formatCurrency(value, currencyCode)}
                                    />
                                    <Bar dataKey="value" fill="#0ea5e9" radius={[0, 6, 6, 0]} barSize={32} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-full text-slate-400">
                                No data available
                            </div>
                        )}
                    </div>
                </div>

                {/* Donut Chart */}
                <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 flex flex-col">
                    <h3 className="text-xl font-bold text-slate-800 mb-8">Κατανομή (Τεμάχια)</h3>
                    <div className="flex-1 relative min-h-[300px]">
                        {/* Center Text */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center z-0 pointer-events-none">
                            <span className="text-5xl font-black text-slate-800">{totalAssets}</span>
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Total Assets</span>
                        </div>

                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="value"
                                        cornerRadius={10}
                                        startAngle={90}
                                        endAngle={-270}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-full text-slate-400">
                                No data available
                            </div>
                        )}
                    </div>

                    {/* Custom Legend */}
                    <div className="mt-8 space-y-3 max-h-40 overflow-y-auto pr-2">
                        {pieData.map((entry, index) => (
                            <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                                    <span className="text-sm font-bold text-slate-600 truncate max-w-[120px]" title={entry.name}>{entry.name}</span>
                                </div>
                                <span className="text-sm font-bold text-slate-900">{((entry.value / totalAssets) * 100).toFixed(1)}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
